/**
 * Auto-hiding scroll indicator.
 *
 * A thin track pinned to the right edge with a thumb sized to the visible
 * fraction of the page. Invisible until you scroll, back out of the way after
 * you stop.
 *
 * THE SCROLLER IS NOT THE DOCUMENT. This theme scrolls .page-wrapper —
 * overflow-y: auto, with html and body both overflow: hidden. Reading
 * window.scrollY or document.scrollingElement here returns 0 forever and the
 * thumb never moves. The container is resolved below and everything hangs off
 * it, with the document as a fallback for any layout that does scroll normally.
 *
 * THE LOOP. The scroll listener stores a number and flags the frame; it is
 * passive and does no reading, no writing and no measuring. One rAF loop moves
 * the thumb with transform: translate3d and nothing else — top/height are
 * layout properties and writing them per frame would re-lay-out the page. The
 * thumb's HEIGHT is written once on measure and again only on resize.
 *
 * The loop is not permanent: it runs while scrolling or dragging and cancels
 * itself once the indicator has faded out again.
 *
 * REDUCED MOTION: nothing is built and the native scrollbar is left alone. The
 * whole point of this component is a thing that appears and disappears.
 *
 * FINE POINTERS ONLY, the whole component. On touch there is no persistent
 * scrollbar to replace: the platform draws its own overlay indicator, so
 * hiding it would give nothing back, and adding this one beside it would be
 * two indicators for one scroll position.
 */
(function () {
  "use strict";

  var still = window.matchMedia("(prefers-reduced-motion: reduce)");
  if (still.matches) return;

  /* Fine pointers only, for the whole component. The brief is to leave the
     native scrollbar alone on touch — and if the platform's own overlay
     indicator stays, adding a second one beside it is two indicators for one
     scroll position. This is a replacement, not an addition. */
  var fine = window.matchMedia("(pointer: fine)");
  if (!fine.matches) return;

  var IDLE = 800;   // ms of no scrolling before it fades
  var MIN_THUMB = 28;

  var scroller = null;
  var track = null;
  var thumb = null;
  var raf = 0;
  var idleTimer = 0;
  var hovering = false;
  var dragging = false;
  var dragOffset = 0;

  var pos = 0;          // written by the scroll listener and nothing else
  var trackH = 0, thumbH = 0, maxScroll = 0;

  function findScroller() {
    var el = document.querySelector(".page-wrapper");
    if (el && el.scrollHeight - el.clientHeight > 4) return el;
    var doc = document.scrollingElement || document.documentElement;
    return doc.scrollHeight - doc.clientHeight > 4 ? doc : null;
  }

  function build() {
    track = document.createElement("div");
    track.className = "cscroll";
    track.setAttribute("aria-hidden", "true");
    thumb = document.createElement("span");
    thumb.className = "cscroll__thumb";
    track.appendChild(thumb);
    document.body.appendChild(track);

    track.addEventListener("pointerenter", function () { hovering = true; show(); });
    track.addEventListener("pointerleave", function () { hovering = false; scheduleHide(); });
    track.addEventListener("pointerdown", onDown);
  }

  /* Measured, not assumed, and only when something could have changed it. */
  function measure() {
    if (!scroller) return;
    trackH = track.clientHeight;
    var visible = scroller.clientHeight;
    var total = scroller.scrollHeight;
    maxScroll = total - visible;
    if (maxScroll <= 0) { track.style.display = "none"; return; }
    track.style.display = "";
    thumbH = Math.max(MIN_THUMB, Math.round(trackH * (visible / total)));
    thumb.style.height = thumbH + "px";
  }

  function apply() {
    var frac = maxScroll > 0 ? pos / maxScroll : 0;
    if (frac < 0) frac = 0;
    if (frac > 1) frac = 1;
    var y = frac * (trackH - thumbH);
    thumb.style.transform = "translate3d(0," + y.toFixed(2) + "px,0)";
  }

  function tick() {
    apply();
    /* Keep going while there is a reason to: mid-drag, or still visible and
       therefore still able to move. Otherwise stop — a loop that runs against
       a stationary page is pure waste. */
    if (dragging || track.classList.contains("is-on")) {
      raf = window.requestAnimationFrame(tick);
    } else {
      raf = 0;
    }
  }

  function start() {
    if (!raf) raf = window.requestAnimationFrame(tick);
  }

  function show() {
    track.classList.add("is-on");
    window.clearTimeout(idleTimer);
    start();
  }

  function scheduleHide() {
    window.clearTimeout(idleTimer);
    if (hovering || dragging) return;
    idleTimer = window.setTimeout(function () {
      track.classList.remove("is-on");
    }, IDLE);
  }

  function onScroll() {
    pos = scroller === document.scrollingElement || scroller === document.documentElement
      ? (window.scrollY || scroller.scrollTop)
      : scroller.scrollTop;
    show();
    scheduleHide();
  }

  /* ---- Drag to scroll ---------------------------------------------------- */

  function onDown(e) {
    if (e.button !== undefined && e.button !== 0) return;
    measure();
    var rect = track.getBoundingClientRect();
    var thumbTop = rect.top + (maxScroll > 0 ? (pos / maxScroll) * (trackH - thumbH) : 0);
    var onThumb = e.clientY >= thumbTop && e.clientY <= thumbTop + thumbH;
    /* Grabbing the thumb keeps the point you grabbed under the pointer;
       clicking the bare track jumps so the thumb centres on the click. */
    dragOffset = onThumb ? e.clientY - thumbTop : thumbH / 2;
    dragging = true;
    track.setPointerCapture(e.pointerId);
    show();
    dragTo(e.clientY);
    e.preventDefault();
  }

  function dragTo(clientY) {
    var rect = track.getBoundingClientRect();
    var travel = trackH - thumbH;
    if (travel <= 0) return;
    var frac = (clientY - rect.top - dragOffset) / travel;
    if (frac < 0) frac = 0;
    if (frac > 1) frac = 1;
    var target = frac * maxScroll;
    pos = target;
    if (scroller === document.scrollingElement || scroller === document.documentElement) {
      window.scrollTo(0, target);
    } else {
      scroller.scrollTop = target;
    }
  }

  function onMove(e) { if (dragging) dragTo(e.clientY); }

  function onUp(e) {
    if (!dragging) return;
    dragging = false;
    try { track.releasePointerCapture(e.pointerId); } catch (err) {}
    scheduleHide();
  }

  function init() {
    scroller = findScroller();
    if (!scroller) return;

    build();
    measure();
    onScroll();
    /* Built, measured, and immediately hidden: it should not announce itself
       on load, only when the page actually moves. */
    track.classList.remove("is-on");

    var target = scroller === document.scrollingElement || scroller === document.documentElement
      ? window : scroller;
    target.addEventListener("scroll", onScroll, { passive: true });

    window.addEventListener("resize", function () { measure(); apply(); }, { passive: true });
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);

    /* Sections load lazily and images decode late, both of which change the
       scroll height the thumb is sized from. */
    if (window.ResizeObserver) {
      new ResizeObserver(function () { measure(); apply(); }).observe(scroller);
    }

    document.documentElement.classList.add("cscroll-native-off");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
